/**
 * The demo's first-party traffic: a fixed set of landing-page nodes, each with a daily
 * rate of sessions and orders, summed over whatever range, level and model the page
 * asks for. Enough to drill every channel down to a landing page, to see the model
 * move orders between channels, and to show the unattributed row.
 *
 * The rules are the API's, approximately: sessions and users are summed across nodes
 * (the API counts them distinct), and a model is a per-channel weight on orders. Days
 * before the tenant's tracking baseline have no tracked visit: their orders are all
 * in the Not tracked row (`credit: 'unattributed'`, the channel level only), and the
 * report carries the `data_quality` block with the site-wide coverage.
 */

import {
  FIRST_PARTY_LEVELS,
  FIRST_PARTY_METRIC_KEYS,
  type AttributionModel,
  type FirstPartyMetricKey,
  type FirstPartyTrafficMetrics,
  type FirstPartyTrafficQuery,
  type FirstPartyTrafficReport,
  type FirstPartyTrafficRow,
} from '@/api/firstPartyTraffic'
import { addDays, dayCount } from '@/lib/filters'
import { demoDataQuality, trackedDays } from '@/mock/dataQuality'


/** The API's channel names (`channels.CHANNEL_LABELS`); the real client reads them off each row. */
const CHANNEL_LABELS: Record<string, string> = {
  direct: 'Direct',
  display: 'Display',
  paid_social: 'Paid social',
  paid_search: 'Paid search',
  paid_other: 'Other paid',
  email: 'Email',
  sms: 'SMS',
  affiliate: 'Affiliates',
  organic_social: 'Organic social',
  organic_search: 'Organic search',
  referral: 'Referral',
  other: 'Other',
}

/** A wire sort field (`revenue_minor`) as the report's own key (`revenueMinor`). */
function sortField(wire: string): FirstPartyMetricKey | 'label' {
  const camel = wire.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase())
  return (FIRST_PARTY_METRIC_KEYS as readonly string[]).includes(camel) ? (camel as FirstPartyMetricKey) : 'label'
}

type Path = readonly [string, string, string, string, string, string]

interface DemoNode {
  path: Path
  sessionsPerDay: number
  ordersPerDay: number
  aovMinor: number
}

const NODES: readonly DemoNode[] = [
  { path: ['paid_social', 'facebook', 'paid_social', 'summer-sellout', 'video-a', '/competitions/porsche-911'], sessionsPerDay: 120, ordersPerDay: 3.2, aovMinor: 2400 },
  { path: ['paid_social', 'facebook', 'paid_social', 'summer-sellout', 'carousel-b', '/competitions/'], sessionsPerDay: 60, ordersPerDay: 1.1, aovMinor: 1800 },
  { path: ['paid_social', 'instagram', 'paid_social', 'summer-sellout', 'story-1', '/competitions/porsche-911'], sessionsPerDay: 45, ordersPerDay: 0.9, aovMinor: 2100 },
  { path: ['paid_social', 'tiktok', 'cpc', 'autumn-draw', '(not set)', '/competitions/cash-10k'], sessionsPerDay: 30, ordersPerDay: 0.4, aovMinor: 1500 },
  { path: ['paid_search', 'google', 'cpc', 'brand', '(not set)', '/'], sessionsPerDay: 80, ordersPerDay: 2.5, aovMinor: 2000 },
  { path: ['organic_search', 'google.co.uk', 'organic', '(not set)', '(not set)', '/'], sessionsPerDay: 150, ordersPerDay: 2, aovMinor: 1700 },
  { path: ['organic_search', 'bing.com', 'organic', '(not set)', '(not set)', '/competitions/'], sessionsPerDay: 20, ordersPerDay: 0.2, aovMinor: 1500 },
  { path: ['organic_social', 'facebook.com', 'referral', '(not set)', '(not set)', '/winners/'], sessionsPerDay: 40, ordersPerDay: 0.3, aovMinor: 1200 },
  { path: ['email', 'newsletter', 'email', 'weekly-draw', 'header-cta', '/competitions/cash-10k'], sessionsPerDay: 55, ordersPerDay: 1.8, aovMinor: 2600 },
  { path: ['sms', 'twilio', 'sms', 'last-chance', '(not set)', '/competitions/porsche-911'], sessionsPerDay: 12, ordersPerDay: 0.6, aovMinor: 3000 },
  { path: ['referral', 'competition-list.example', 'referral', '(not set)', '(not set)', '/competitions/'], sessionsPerDay: 25, ordersPerDay: 0.35, aovMinor: 1600 },
  { path: ['affiliate', 'compare-comps', 'affiliate', 'partner-q3', 'banner-1', '/competitions/'], sessionsPerDay: 15, ordersPerDay: 0.3, aovMinor: 2000 },
  { path: ['direct', '(direct)', '(none)', '(not set)', '(not set)', '/'], sessionsPerDay: 200, ordersPerDay: 2.8, aovMinor: 1900 },
]

/** How much of each channel's orders a model credits it, against last non-direct. */
const MODEL_WEIGHTS: Record<AttributionModel, Partial<Record<string, number>>> = {
  last_non_direct: { direct: 0.35 },
  last_touch: { direct: 1.6, email: 1.2, paid_social: 0.85, organic_search: 0.9 },
  first_touch: { direct: 0.6, organic_search: 1.3, paid_social: 1.15, email: 0.9 },
  paid_click: { direct: 0.35, paid_social: 1.2, paid_search: 1.15, organic_search: 0.9, email: 0.9 },
}

const UNATTRIBUTED_ORDERS_PER_DAY = 0.4
const UNATTRIBUTED_AOV_MINOR = 1500
/** The previous period is a little quieter, so the deltas have something to say. */
const PREVIOUS_FACTOR = 0.88

interface Sums {
  sessions: number
  orders: number
  revenueMinor: number
}

function metrics({ sessions, orders, revenueMinor }: Sums): FirstPartyTrafficMetrics {
  return {
    sessions,
    users: Math.round(sessions * 0.82),
    orders,
    revenueMinor,
    newCustomers: Math.round(orders * 0.4),
    conversionRate: sessions > 0 ? orders / sessions : null,
    aovMinor: orders > 0 ? Math.round(revenueMinor / orders) : null,
  }
}

function nodeSums(node: DemoNode, model: AttributionModel, days: number, factor: number): Sums {
  const weight = MODEL_WEIGHTS[model][node.path[0]] ?? 1
  const orders = Math.round(node.ordersPerDay * weight * days * factor)
  return { sessions: Math.round(node.sessionsPerDay * days * factor), orders, revenueMinor: orders * node.aovMinor }
}

function add(into: Sums, more: Sums): Sums {
  return { sessions: into.sessions + more.sessions, orders: into.orders + more.orders, revenueMinor: into.revenueMinor + more.revenueMinor }
}

const ZERO: Sums = { sessions: 0, orders: 0, revenueMinor: 0 }

/** Every node under `path`, summed by its key at `depth`, plus the parent's total. */
function grouped(query: FirstPartyTrafficQuery, days: number, factor: number): { byKey: Map<string, Sums>; total: Sums } {
  const depth = FIRST_PARTY_LEVELS.indexOf(query.level)
  const byKey = new Map<string, Sums>()
  let total = ZERO
  for (const node of NODES) {
    if (!query.path.every((key, index) => node.path[index] === key)) continue
    const sums = nodeSums(node, query.model, days, factor)
    const key = node.path[depth] ?? ''
    byKey.set(key, add(byKey.get(key) ?? ZERO, sums))
    total = add(total, sums)
  }
  return { byKey, total }
}

/** The orders with no tracked touch: the lookback's no-touch rate every day, and every order on a day before the baseline. */
function notTracked(model: AttributionModel, days: number, tracked: number, factor: number): Sums {
  const orders = Math.round(UNATTRIBUTED_ORDERS_PER_DAY * days * factor)
  let lost: Sums = { sessions: 0, orders, revenueMinor: orders * UNATTRIBUTED_AOV_MINOR }
  for (const node of NODES) lost = add(lost, { ...nodeSums(node, model, days - tracked, factor), sessions: 0 })
  return lost
}

function sortRows(rows: FirstPartyTrafficRow[], sort: string | undefined): FirstPartyTrafficRow[] {
  const wire = sort ?? '-revenue_minor'
  const descending = wire.startsWith('-')
  const field = sortField(wire.replace(/^-/, ''))
  const value = (row: FirstPartyTrafficRow): string | number | null => (field === 'label' ? row.label.toLowerCase() : row[field])
  const direction = descending ? -1 : 1
  return [...rows].sort((a, b) => {
    const [left, right] = [value(a), value(b)]
    if (left === null || right === null) return left === right ? 0 : left === null ? 1 : -1
    return left < right ? -direction : left > right ? direction : a.label.localeCompare(b.label)
  })
}

export function demoFirstPartyTraffic(
  query: FirstPartyTrafficQuery,
  currency: string,
  now: Date,
  baseline: string | null = null,
): FirstPartyTrafficReport {
  const days = dayCount(query.from, query.to)
  const previousEnd = addDays(query.from, -1)
  const previousStart = addDays(previousEnd, -(days - 1))
  const tracked = trackedDays(query.from, query.to, baseline)
  const trackedBefore = trackedDays(previousStart, previousEnd, baseline)
  const current = grouped(query, tracked, 1)
  const before = query.compare ? grouped(query, trackedBefore, PREVIOUS_FACTOR) : null
  const top = query.path.length === 0
  const lost = notTracked(query.model, days, tracked, 1)
  const lostBefore = notTracked(query.model, days, trackedBefore, PREVIOUS_FACTOR)
  const row = (key: string | null, label: string, sums: Sums, then: Sums | null): FirstPartyTrafficRow => ({
    ...metrics(sums),
    key,
    credit: key === null ? 'unattributed' : 'touch',
    label,
    path: [...query.path],
    model: query.model,
    tmtAttributed: true,
    previous: then ? metrics(then) : null,
  })
  const keys = new Set([...current.byKey.keys(), ...(before?.byKey.keys() ?? [])])
  const rows = sortRows(
    [...keys].map((key) =>
      row(key, query.level === 'channel' ? (CHANNEL_LABELS[key] ?? key) : key, current.byKey.get(key) ?? ZERO, before ? (before.byKey.get(key) ?? ZERO) : null),
    ),
    query.sort,
  )
  if (top && lost.orders > 0) rows.push(row(null, 'Unattributed (no touch in the lookback)', lost, before ? lostBefore : null))
  // Coverage is site-wide for the range, never narrowed by the drill-down parent (as the API's).
  const site = grouped({ ...query, level: 'channel', path: [] }, tracked, 1).total
  const offset = query.offset ?? 0
  return {
    source: 'tmt',
    tmtAttributed: true,
    model: query.model,
    level: query.level,
    start: query.from,
    end: query.to,
    compared: query.compare,
    previousStart: query.compare ? previousStart : null,
    previousEnd: query.compare ? previousEnd : null,
    environments: [...query.environments],
    nonProduction: query.environments.some((environment) => environment !== 'production'),
    currency,
    lookbackDays: 30,
    consentNote:
      "Sessions and users are the website's own visits from visitors who allowed analytics. They are not Google Analytics sessions and will not match GA4's figures.",
    totalRows: rows.length,
    rows: rows.slice(offset, offset + (query.limit ?? rows.length)),
    totals: metrics(top ? add(current.total, lost) : current.total),
    totalsPrevious: before ? metrics(top ? add(before.total, lostBefore) : before.total) : null,
    freshness: { newestTouchAt: new Date(now.getTime() - 7 * 60_000).toISOString() },
    dataQuality: demoDataQuality({
      baseline,
      from: query.from,
      previousFrom: query.compare ? previousStart : null,
      orders: site.orders + lost.orders,
      notTrackedOrders: lost.orders,
    }),
  }
}

const CSV_METRICS = ['sessions', 'users', 'orders', 'revenue_minor', 'new_customers', 'conversion_rate', 'aov_minor'] as const

/** The API's CSV columns (`routes.render_csv`), for the demo download. */
function demoCsv(report: FirstPartyTrafficReport): string {
  const header = ['level', 'key', 'label', 'path', ...CSV_METRICS, 'currency', 'source', 'model', 'credit', 'tracked_share', 'tracking_reliable_from']
  const lines = report.rows.map((row) =>
    [
      report.level,
      row.key ?? '',
      row.label,
      row.path.join(' > '),
      ...CSV_METRICS.map((name) => row[sortField(name) as FirstPartyMetricKey] ?? ''),
      report.currency,
      report.source,
      report.model,
      row.credit ?? '',
      report.dataQuality?.coverage?.trackedShare ?? '',
      report.dataQuality?.trackingReliableFrom ?? '',
    ]
      .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
      .join(','),
  )
  return [header.join(','), ...lines].join('\n') + '\n'
}

export function demoFirstPartyTrafficCsv(query: FirstPartyTrafficQuery, currency: string, now: Date, baseline: string | null = null): Blob {
  const report = demoFirstPartyTraffic({ ...query, limit: undefined, offset: 0 }, currency, now, baseline)
  return new Blob([demoCsv(report)], { type: 'text/csv' })
}
